import * as React from 'react';

/**
 * Infinite-scroll sentinel: attach the returned ref to an element after the list; `onVisible` runs when it comes
 * within `rootMargin` of the viewport while `enabled`. The observer is rebuilt whenever `enabled` turns on again
 * (e.g. after a page finished loading), so a sentinel that is still on screen fires once more and short pages keep
 * loading until the list fills the viewport or `enabled` goes false.
 */
export function useIntersectionSentinel(
    onVisible: () => void,
    enabled: boolean,
    rootMargin = '400px',
): (element: Element | null) => void {
    const [element, setElement] = React.useState<Element | null>(null);
    const callbackRef = React.useRef(onVisible);
    callbackRef.current = onVisible;

    React.useEffect(() => {
        if (!enabled || element == null || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) callbackRef.current();
        }, { rootMargin });
        observer.observe(element);
        return () => observer.disconnect();
    }, [enabled, element, rootMargin]);

    return setElement;
}
